import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalController } from '@ionic/angular/standalone';

import { Subscription } from 'rxjs';

import { Store } from '@ngxs/store';

import { LeaderboardState } from 'projects/tools/src/lib/shared-module/leaderboard/store/leaderboard.state';
import { LeaderboardAssociationModel } from 'projects/tools/src/lib/shared-module/leaderboard/store/leaderboard-association.model';
import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { PuzzleFrameworkService } from 'projects/tools/src/lib/puzzle-framework-module/puzzle-framework.service';
import { LeaderboardInfoModalComponent } from 'projects/tools/src/lib/shared-module/leaderboard-info-modal/leaderboard-info-modal.component';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ng2-tooltip-directive-major-angular-updates';

@Component({
  selector: 'lib-app-leaderboard-info',
  templateUrl: './leaderboard-info.component.html',
  styleUrls: ['./leaderboard-info.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule],
})
export class LeaderboardInfoComponent implements OnDestroy {
  private modalOpen = false;

  public isFinished = false;
  public isSubmitted = false;
  public submittedStrictRules = false;
  private puzzleIDSubscription: Subscription;
  private leaderboardAssociationSubscription: Subscription;

  public withinLeaderboardSubmissionTime = false;
  public strictRulesActive = false;
  public submissionPolicyViolation = '';

  constructor(
    private framework: PuzzleFrameworkService,
    private ionicModal: ModalController,
    private store: Store,
    public leaderboard: LeaderboardService
  ) {
    this.puzzleIDSubscription = this.store
      .select((state) => state.puzzleID)
      .subscribe((puzzleId) => {
        if (puzzleId) {
          if (this.leaderboardAssociationSubscription) {
            this.leaderboardAssociationSubscription.unsubscribe();
          }
          this.leaderboardAssociationSubscription = this.store
            .select(LeaderboardState.getByID(puzzleId))
            .subscribe((leaderboardAssociation: LeaderboardAssociationModel) => {
              if (leaderboardAssociation) {
                if (leaderboardAssociation.submittedEntryUUID !== undefined) {
                  this.isSubmitted = true;
                  this.submittedStrictRules = leaderboardAssociation.submittedEntry.strictRules;
                }
                this.isFinished = leaderboardAssociation.puzzleIsFinished;
              }
            });
        }
      });

    this.leaderboard.withinLeaderboardSubmissionTime$.subscribe((withinLeaderboardSubmissionTime) => {
      this.withinLeaderboardSubmissionTime = withinLeaderboardSubmissionTime;
    });
    this.leaderboard.strictRulesActive$.subscribe((strictRulesActive) => {
      this.strictRulesActive = strictRulesActive;
    });
    this.leaderboard.submissionPolicyViolation$.subscribe((submissionPolicyViolation) => {
      this.submissionPolicyViolation = submissionPolicyViolation;
    });
  }

  public async showLeaderboardInfoModal(event) {
    event.preventDefault();

    if (this.modalOpen) {
      return;
    }
    this.modalOpen = true;

    if (this.leaderboard.canPauseSilently()) {
      this.framework.pauseCurrentGame();
    }

    const modal = await this.ionicModal.create({
      component: LeaderboardInfoModalComponent,
      backdropDismiss: true,
      cssClass: ['puzzle-modal', 'puzzle-modal-large'],
    });

    await modal.present();

    const result = await modal.onDidDismiss();

    this.modalOpen = false;
    this.framework.resumeCurrentGame();

    return true;
  }

  ngOnDestroy() {
    if (this.puzzleIDSubscription) {
      this.puzzleIDSubscription.unsubscribe();
    }
    if (this.leaderboardAssociationSubscription) {
      this.leaderboardAssociationSubscription.unsubscribe();
    }
  }

  tooltip() {
    let tooltip = '';
    if (!this.withinLeaderboardSubmissionTime) {
      tooltip = 'PUZZLE.leaderboard_info.tooltip_' + (this.strictRulesActive ? 'strict' : 'relaxed') + '_old';
    }
    if (this.submissionPolicyViolation === 'first-only' && !this.isSubmitted) {
      tooltip = 'PUZZLE.leaderboard_info.tooltip_violation_first_only';
    }
    if (this.isFinished && !this.isSubmitted && this.withinLeaderboardSubmissionTime) {
      tooltip = 'PUZZLE.leaderboard_info.tooltip_solved_' + (this.strictRulesActive ? 'strict' : 'relaxed');
    }
    if (this.isSubmitted && this.withinLeaderboardSubmissionTime) {
      tooltip =
        'PUZZLE.leaderboard_info.tooltip_' +
        (this.strictRulesActive ? 'strict' : 'relaxed') +
        '_submitted_' +
        (this.submittedStrictRules ? 'strict' : 'relaxed');
    }
    if (this.isSubmitted && !this.withinLeaderboardSubmissionTime) {
      tooltip = 'PUZZLE.leaderboard_info.submitted_old';
    }
    if (!this.isFinished && !this.isSubmitted && this.withinLeaderboardSubmissionTime) {
      tooltip = 'PUZZLE.leaderboard_info.tooltip_' + (this.strictRulesActive ? 'strict' : 'relaxed');
    }
    return tooltip;
  }
}
